/**
 * LifecycleHistoryPanel — paginated lifecycle event journal.
 *
 * Surfaces the synthetic Phase-8 receipt stream as a navigable log: the
 * reader pages through history with the Pagination control, sees each
 * approver via an Avatar, switches between phase-grouped views with
 * AnimatedSwap, and watches the event list re-order through an
 * AutoAnimateList so additions / phase changes animate by construction.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useCallback, useMemo, useState } from "react";
import {
  AnimatedSwap,
  AutoAnimateList,
  Avatar,
  Pagination,
} from "@csk/demo-ui";
import { PanelSurface, panelTokens } from "@csk/demo-ui";
import historyData from "../data/lifecycleHistory.json" with { type: "json" };

interface PhaseSpec {
  readonly id: string;
  readonly label: string;
  readonly color: string;
}

interface Approver {
  readonly id: string;
  readonly name: string;
  readonly role: string;
}

interface LifecycleEvent {
  readonly id: string;
  readonly timestamp: string;
  readonly invariantId: string;
  readonly gate: string;
  readonly phase: string;
  readonly approverId: string;
  readonly summary: string;
  readonly outcome: "ACCEPT" | "REFUSE";
}

const PHASES: readonly PhaseSpec[] = historyData.phases;
const APPROVERS: readonly Approver[] = historyData.approvers;
const EVENTS: readonly LifecycleEvent[] =
  historyData.events as readonly LifecycleEvent[];
const PAGE_SIZE = 5;
type PhaseFilter = string;

const APPROVER_BY_ID: ReadonlyMap<string, Approver> = new Map(
  APPROVERS.map((entry) => [entry.id, entry]),
);
const PHASE_BY_ID: ReadonlyMap<string, PhaseSpec> = new Map(
  PHASES.map((entry) => [entry.id, entry]),
);
const SORTED_EVENTS = [...EVENTS].sort((a, b) => {
  if (a.timestamp === b.timestamp) return 0;
  return a.timestamp < b.timestamp ? 1 : -1;
});
const PHASE_EVENT_COUNTS = new Map<string, number>();
for (const event of EVENTS) {
  PHASE_EVENT_COUNTS.set(
    event.phase,
    (PHASE_EVENT_COUNTS.get(event.phase) ?? 0) + 1,
  );
}
const PHASE_CHIPS = [
  {
    id: "all",
    label: `all (${EVENTS.length.toString()})`,
    color: panelTokens.muted,
  },
  ...PHASES.map((entry) => ({
    id: entry.id,
    label: `${entry.label} (${(PHASE_EVENT_COUNTS.get(entry.id) ?? 0).toString()})`,
    color: entry.color,
  })),
] as const;

function approverFor(id: string): Approver {
  return APPROVER_BY_ID.get(id) ?? APPROVERS[0];
}

function phaseColor(id: string): string {
  return PHASE_BY_ID.get(id)?.color ?? panelTokens.accent;
}

function formatTimestamp(iso: string): string {
  // Render in UTC so the panel is hydration-stable across timezones; the
  // raw ISO string carries the canonical instant if the reader needs it.
  return iso.replace("panelTokens", " ").replace("Z", " UTC");
}

export function LifecycleHistoryPanel(): React.JSX.Element {
  const [phaseFilter, setPhaseFilter] = useState<PhaseFilter>("all");
  const [page, setPage] = useState<number>(1);

  const filteredEvents = useMemo(
    () =>
      phaseFilter === "all"
        ? SORTED_EVENTS
        : SORTED_EVENTS.filter((entry) => entry.phase === phaseFilter),
    [phaseFilter],
  );

  const pageCount = Math.max(1, Math.ceil(filteredEvents.length / PAGE_SIZE));
  const clampedPage = Math.min(page, pageCount);
  const pageEvents = useMemo(
    () =>
      filteredEvents.slice(
        (clampedPage - 1) * PAGE_SIZE,
        clampedPage * PAGE_SIZE,
      ),
    [filteredEvents, clampedPage],
  );

  const handleFilterChange = useCallback((next: string) => {
    setPhaseFilter(next);
    setPage(1);
  }, []);

  return (
    <PanelSurface.PanelShell
      title="Lifecycle History"
      subtitle="Paginated Phase-8 receipt stream with approver identities and phase views"
      accentColor={panelTokens.accentBlue}
    >
      <PanelSurface.Card glowColor="rgba(94,234,212,0.10)">
        <p className="m-0 text-[13.5px] leading-[1.75] text-[var(--ilg-text)]">
          Every row is a receipt the kernel would have emitted under Phase-8:
          which gate it cleared, who signed the ICAP-bound witness, and the
          fingerprint that survives into the next preservation cycle. Switch the
          phase view to watch the same population re-cohort around{" "}
          <em>established</em>, <em>preserved</em>, <em>bridged</em>, or{" "}
          <em>refused</em>; the underlying receipts are unchanged — only the
          slicing changes.
        </p>
      </PanelSurface.Card>

      <PanelSurface.SectionHeading>Phase view</PanelSurface.SectionHeading>
      <PanelSurface.ChipRow
        ariaLabel="Filter by lifecycle phase"
        activeId={phaseFilter}
        onChange={handleFilterChange}
        items={PHASE_CHIPS}
      />

      <PanelSurface.SectionHeading>
        Animated phase swap
      </PanelSurface.SectionHeading>
      <AnimatedSwap itemKey={phaseFilter} direction="fade" durationMs={220}>
        <PanelSurface.Card
          glowColor={`${
            phaseFilter === "all" ? panelTokens.accent : phaseColor(phaseFilter)
          }22`}
          style={{
            borderColor: `${
              phaseFilter === "all"
                ? panelTokens.accent
                : phaseColor(phaseFilter)
            }40`,
          }}
        >
          <p className="m-0 text-[13px] text-[var(--ilg-text)] leading-[1.6]">
            <strong className="text-[var(--ilg-bright)]">
              {phaseFilter === "all"
                ? "All phases"
                : PHASE_BY_ID.get(phaseFilter)?.label}
              :
            </strong>{" "}
            showing {filteredEvents.length.toString()} of{" "}
            {EVENTS.length.toString()} receipts. Page {clampedPage.toString()}{" "}
            of {pageCount.toString()}.
          </p>
        </PanelSurface.Card>
      </AnimatedSwap>

      <PanelSurface.SectionHeading>
        Receipts (page {clampedPage.toString()})
      </PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <AutoAnimateList
          as="ul"
          className="csk-ilg-lifecycle-events"
          durationMs={240}
        >
          {pageEvents.map((event) => {
            const approver = approverFor(event.approverId);
            const phase = PHASE_BY_ID.get(event.phase);
            const accent = phaseColor(event.phase);
            return (
              <li
                key={event.id}
                className="list-none flex gap-3 items-start px-2 py-2.5 border-t border-b border-[var(--ilg-border)] rounded-lg bg-[rgba(6,8,16,0.4)] mb-2"
                style={{
                  borderLeft: `3px solid ${accent}`,
                }}
              >
                <Avatar
                  name={approver.name}
                  id={approver.id}
                  size="md"
                  ariaLabel={`${approver.name} (${approver.role})`}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap gap-1.5 items-center mb-1.5">
                    {PanelSurface.badge(event.gate, accent)}
                    {phase !== undefined &&
                      PanelSurface.badge(phase.label, accent)}
                    {PanelSurface.badge(
                      event.outcome,
                      event.outcome === "REFUSE"
                        ? panelTokens.accentRed
                        : panelTokens.accentGreen,
                    )}
                    <code className="font-mono text-[11px] text-[var(--ilg-bright)]">
                      {event.invariantId}
                    </code>
                  </div>
                  <p className="mt-0 mx-0 mb-1 text-[12.5px] text-[var(--ilg-text)] leading-[1.55]">
                    {event.summary}
                  </p>
                  <div className="text-[11px] text-[var(--ilg-muted)] font-mono">
                    {formatTimestamp(event.timestamp)} · {approver.name} ·{" "}
                    {approver.role}
                  </div>
                </div>
              </li>
            );
          })}
        </AutoAnimateList>
        {pageEvents.length === 0 && (
          <p className="m-0 text-[12.5px] text-[var(--ilg-muted)] italic">
            No receipts match this phase view.
          </p>
        )}
      </PanelSurface.Card>

      <PanelSurface.SectionHeading>Pagination</PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <Pagination
          page={clampedPage}
          pageCount={pageCount}
          onPageChange={setPage}
          ariaLabel="Lifecycle event pages"
          siblingCount={1}
        />
      </PanelSurface.Card>
    </PanelSurface.PanelShell>
  );
}
